import React, { memo, useRef, useEffect, useMemo, Fragment } from 'react';
import moment from 'moment';
import Checkbox from '@material-ui/core/Checkbox';
import { generatePDF } from '@abhisek507/html2multipagepdf';
import {
  Letter,
  Quality as ImageQuality,
  RegularPageSelector as pageSelector,
  PageElementSelector as pageElementSelector,
  PageWithMaxPossibleWidthSelector as pageWithMaxPossibleWidthSelector,
} from '@abhisek507/html2multipagepdf/constants';
import Input from '../../subComponents/input';
import Button from '../../subComponents/button';
import TextAreaWrapper from '../../subComponents/text-area';
import DateWrapper from '../../subComponents/date-wrapper';
import { SectionLabel, SignatureWrapper, trimDots, QuestionAnswers } from '../Eversense';
import { PatientInformation, SectionC } from '../Apretude';
import {
  DateFormat,
  CheckBoxChecked,
  DEFAULT_FORMFIELD,
  MaxDate,
  MaxMonthAndYear,
  DEFAULT_PDF_INFO,
  DEFAULT_FORMFIELD_WHEN_FIELD_IS_MISSING,
} from '../../../constants/cmnTemplateForEversense.constant';
import {
  PID_ProvidersAndCreatePDF,
  QID_NewToVyeptiCheckbox,
  QID_MigrainStartDateInput,
  QID_HeadacheDaysPerMonthInput,
  QID_LastTreatmentDateInput,
  QID_DrugAllergiesInput,
  QID_PreviousMigraineMedicationInput,
  DEFAULT_FORMFIELD_ONLY_FOR_VYEPTI,
} from '../../../constants/cmnTemplateForVyepti.constant';
import { preparePDF } from '../../../utils/referralForm';
import { hasFullfilledOrder } from '../../../utils/productStatus';
import { round } from "../../../utils/time";
import VyeptiLogo from '../../../assets/vyeptiLogo.png';
import './styles.css';

export const CMNVyeptiTemplateIdentifier = 'cmn-template-for-vyepti';

/**
 * @param {{ AnswerText: string }} qAndA 
 * @returns {{ hasAnswer: boolean, answer: number }}
 */
export const evaluateAnswerFromHeadacheDaysInput = (qAndA = { AnswerText: '' }) => {
  try {
    const text = (qAndA.AnswerText || '').trim();
    if (!text?.length) {
      return { hasAnswer: false, answer: 0 };
    }

    const trimmedText = trimDots(text);
    const hasNumber = !isNaN(trimmedText);
    if (!hasNumber) {
      return { hasAnswer: false, answer: 0 };
    }

    const answer = Number(trimmedText);
    return { hasAnswer: (answer >= 0 && answer <= 31), answer };
  } catch(e) {
    return { hasAnswer: false, answer: 0 };
  }
};

/**
 * @param {{ AnswerText: string }} qAndA 
 * @returns {{ hasAnswer: boolean, answer: string }}
 */
export const evaluateAnswerFromLastTreatmentDateInput = (qAndA) => {
  try {
    const [_m, _y] = qAndA.AnswerText.split('/');
    const month = Number(_m);
    if (!month) {
      return { hasAnswer: false, answer: '' };
    }

    const year = Number(_y);
    if (!year) {
      return { hasAnswer: false, answer: '' };
    }

    return { hasAnswer: true, answer: `${year}-${round(month)}` };
  } catch(e) {
    return { hasAnswer: false, answer: '' };
  }
};

const Heading = memo(({
  value: {
    sentTo,
    sentDate,
  },
}) => (
  <div className='heading flex flex-column align-items-center justify-content-space-between gap-8'>
    <div className='flex heading-logo'>
      <div className='flex align-items-center justify-content-center'>
        <img src={VyeptiLogo} alt='vyepti_logo' className='height-60' />
      </div>
    </div>

    <div className='flex flex-column min-width-40 align-items-flex-start'>
      <span className='font-12 color-vyepti-red bold tracking-wider'>Telehealth Infusion Referral Form</span>
    </div>

    <div className='min-width-180'></div>
  </div>
));

const Footer = memo(({
  onSubmit: handleSubmit = () => {}
}) => (
  <div className='footer flex justify-content-flex-end m-t-4 d-none'>
    <Button
      variant='contained'
      color='primary'
      onClick={handleSubmit}
      label='Download Template'
    />
  </div>
));

const PrescriptionInformation = memo(({
  value: {
    quantity,
    refills,
    infuse,
    ...rest
  },
  onChange: handleChange = () => {}
}) => (
  <div className='section prescriber-information m-t-8 m-b-5'>
    <SectionLabel display='PRESCRIPTION/ORDER INFORMATION' />

    <div className='flex flex-column gap-8 p-4'>
      <div className='flex align-items-center gap-8'>
        <div className='flex gap-8 align-items-center'>
          <label className='input-label'>Rx:</label>
        </div>

        <div className='flex align-items-center'>
          <Checkbox
            title="checkbox-vyepti-single-use"
            className='checkbox'
            inputProps={{ 'aria-label': 'primary checkbox' }}
            checked={rest['checkbox-vyepti-single-use']}
            onChange={(_, _value) => { handleChange('checkbox-vyepti-single-use', { target: { value: _value } }); }}
          />
          <label className='font-roboto font-light font-14 line-20'>VYEPTI 100 mg/mL Single-Use Vial</label>
        </div>
      </div>

      <div className='flex align-items-center gap-16'>
        <div className='flex align-items-center'>
          <Input name='quantity' label='Quantity (Vials):' className='flex' value={quantity} onChange={handleChange.bind(this, 'quantity')} />
        </div>

        <div className='flex align-items-center'>
          <Input name='refills' label='Refills:' className='flex' value={refills} onChange={handleChange.bind(this, 'refills')} />
        </div>
      </div>

      <div className='flex flex-column gap-4'>
        <div className='flex align-items-center gap-8'>
          <div className='flex align-items-center'>
            <Input name='infuse' label='SIG: Infuse' className='flex' value={infuse} onChange={handleChange.bind(this, 'infuse')} />
          </div>

          <div className='flex align-items-center font-roboto font-light font-14'>
            mg over ~30 min once every 3 months
          </div>
        </div>

        <div className='flex align-items-center font-12'>
          Refer to page 2 of the PI for further instructions
        </div>
      </div>

      <div className='flex align-items-center gap-4 d-none'>
        <Checkbox
          title="checkbox-infusion-instructions"
          className='checkbox'
          inputProps={{ 'aria-label': 'primary checkbox' }}
          checked={rest['checkbox-infusion-instructions']}
          onChange={(_, _value) => { handleChange('checkbox-infusion-instructions', { target: { value: _value } }); }}
        />
        <label className='font-roboto font-light font-14 line-20'>
          <span className='input-label m-r-2'>Infusion Instructions:</span>
          Withdraw 1 mL of VYEPTI (for 100 mg dose) for 3 mLs (for 300 mg dose) using a sterile needle and syringe and injection into a 100 mL bag of 0.9% Sodium chloride injection, USP. Gently invert the VYEPTI solution to mix completely. Infuse over ~30 minutes. After infusion is complete, flush the line with 20 mL of 0.9% Solium chloride injection, USP.
        </label>
      </div>

      <div className='flex align-items-center gap-4'>
        <Checkbox
          title="checkbox-supplies"
          className='checkbox'
          inputProps={{ 'aria-label': 'primary checkbox' }}
          checked={rest['checkbox-supplies']}
          onChange={(_, _value) => { handleChange('checkbox-supplies', { target: { value: _value } }); }}
        />
        <label className='font-roboto font-light font-14 line-20'>
          <span className='input-label m-r-2'>Supplies:</span>
          Provide necessary infusion and flush supplies.
        </label>
      </div>

      <div className='flex align-items-center gap-4'>
        <Checkbox
          title="checkbox-anaphylaxis-protocol"
          className='checkbox'
          inputProps={{ 'aria-label': 'primary checkbox' }}
          checked={rest['checkbox-anaphylaxis-protocol']}
          onChange={(_, _value) => { handleChange('checkbox-anaphylaxis-protocol', { target: { value: _value } }); }}
        />
        <label className='font-roboto font-light font-14 line-20'>
          <span className='input-label m-r-2'>Anaphylaxis Protocol:</span>
          Administer anaphylaxis protocol as needed.
        </label>
      </div>

      <div className='flex align-items-center gap-4'>
        <Checkbox
          title="checkbox-iv-flush"
          className='checkbox'
          inputProps={{ 'aria-label': 'primary checkbox' }}
          checked={rest['checkbox-iv-flush']}
          onChange={(_, _value) => { handleChange('checkbox-iv-flush', { target: { value: _value } }); }}
        />
        <label className='font-roboto font-light font-14 line-20'>
          <span className='input-label m-r-2'>IV Flush:</span>
          Flush with normal saline according to protocol.
        </label>
      </div>

      <div className='flex align-items-center gap-4'>
        <Checkbox
          title="checkbox-premedication"
          className='checkbox'
          inputProps={{ 'aria-label': 'primary checkbox' }}
          checked={rest['checkbox-premedication']}
          onChange={(_, _value) => { handleChange('checkbox-premedication', { target: { value: _value } }); }}
        />
        <div className='flex flex-column'>
          <label className='font-roboto font-light font-14 line-20'>
            <span className='input-label m-r-2'>Premedication:</span>
            Administer as needed.
          </label>

          <span className='flex align-items-center font-12'>
            Substitutions Allowed
          </span>
        </div>
      </div>
    </div>
  </div>
));

const PrescriberInformation = memo(({
  value: {
    prescriberFullName,
    prescriberNpi,
    prescriberAddress,
    prescriberState,
    prescriberZip,
    prescriberCity,
    prescriberPhone,
    prescriberFax,
  },
  onChange: handleChange = () => {}
}) => (
  <div className='section prescriber-information m-t-8 m-b-5'>
    <SectionLabel display='Ordering Healthcare Provider (HCP)' />

    <div className='flex flex-column gap-8 p-4'>
      <div className='flex align-items-center'>
        <Input name='prescriberFullName' label='HCP Last, First Name:' className='flex flex-fill m-r-4'  value={prescriberFullName} onChange={handleChange.bind(this, 'prescriberFullName')} />
        <Input name='prescriberNpi' label='NPI #:' value={prescriberNpi} onChange={handleChange.bind(this, 'prescriberNpi')} />
      </div>

      <div className='flex align-items-center'>
        <Input name='prescriberAddress' label='HCP Address:' className='flex flex-fill m-r-4' value={prescriberAddress} />
        <Input
          name='prescriberState'
          label='State:'
          className='m-r-4'
          inputClassName='max-width-165'
          value={prescriberState}
        />
        <Input
          type='text'
          name='prescriberZip'
          label='Zip:'
          inputClassName='max-width-90'
          value={prescriberZip}
        />
      </div>

      <div className='flex align-items-center'>
        <Input name='prescriberCity' label='City:' className='flex flex-fill m-r-4' value={prescriberCity} />
        <Input name='prescriberPhone' label='Phone:' className='m-r-4' value={prescriberPhone} />
        <Input name='prescriberFax' label='Fax:' value={prescriberFax} />
      </div>
    </div>
  </div>
));

const MedicalNecessity = memo(({
  value: {
    icd10Diagnosis,
    migrainStartDate,
    headacheDaysPerMonth,
    lastTreatmentDate,
    drugAllergies,
    previousMigraineMedication,
    ...rest
  },
  onChange: handleChange = () => {}
}) => (
  <div className='section prescriber-information m-t-8 m-b-5'>
    <SectionLabel display='MEDICAL NECESSITY' />

    <div className='flex flex-column'>
      <div className='flex flex-fill font-14 p-l-4 p-t-2 p-b-2 p-r-4 bg-light-primary'>See attached clinical notes for details.</div>
    </div>

    <div className='flex flex-column gap-8 p-4'>
      <div className='flex align-items-center gap-16'>
        <div className='flex align-items-center gap-8'>
          <div className='flex gap-8 align-items-center'>
            <label className='input-label'>Patient is New to VYEPTI?</label>
          </div>

          <div className='flex align-items-center width-60'>
            <Checkbox
              title="checkbox-new-patient-yes"
              className='checkbox'
              inputProps={{ 'aria-label': 'primary checkbox' }}
              checked={rest['checkbox-new-patient'] === CheckBoxChecked.yes}
              onChange={(_, _value) => { handleChange('checkbox-new-patient', { target: { value: _value ? CheckBoxChecked.yes : '' } }); }}
            />
            <label>Yes</label>
          </div>

          <div className='flex align-items-center width-60'>
            <Checkbox
              title="checkbox-new-patient-no"
              className='checkbox'
              inputProps={{ 'aria-label': 'primary checkbox' }}
              checked={rest['checkbox-new-patient'] === CheckBoxChecked.no}
              onChange={(_, _value) => { handleChange('checkbox-new-patient', { target: { value: _value ? CheckBoxChecked.no : '' } }); }}
            />
            <label>No</label>
          </div>
        </div>

        <div className='flex align-items-center'>
          <DateWrapper
            name='migrainStartDate'
            label='Date Migraines Started:'
            value={migrainStartDate}
            max={MaxMonthAndYear}
            type='month'
            onChange={handleChange.bind(this, 'migrainStartDate')}
          />
        </div>
      </div>

      <div className='flex align-items-center gap-8'>
        <div className='flex col-1 align-items-center gap-4'>
          <span className='input-label'>Primary Diagnosis</span>
          <span className='italic font-14'>(ICD-10 Code):</span>
          <span className='required font-24'>*</span>
        </div>
        <Input name='icd10Diagnosis' label='' className='flex flex-fill' value={icd10Diagnosis} onChange={handleChange.bind(this, 'icd10Diagnosis')} />
      </div>

      <div className='flex align-items-center gap-16'>
        <div className='flex align-items-center'>
          <Input
            name='headacheDaysPerMonth'
            label='Headache Days Per Month:'
            inputClassName='max-width-120'
            value={headacheDaysPerMonth}
            onChange={handleChange.bind(this, 'headacheDaysPerMonth')}
          />
        </div>
        <div className='flex align-items-center'>
          <DateWrapper
            name='lastTreatmentDate'
            label='Date of Last Treatment:'
            value={lastTreatmentDate}
            max={MaxMonthAndYear}
            type='month'
            onChange={handleChange.bind(this, 'lastTreatmentDate')}
          />
        </div>
      </div>

      <TextAreaWrapper
        name='drugAllergies'
        label='Allergies:'
        value={drugAllergies}
        onChange={handleChange.bind(this, 'drugAllergies')}
      />

      <TextAreaWrapper
        name='previousMigraineMedication'
        label='Previous Migraine Medication:'
        value={previousMigraineMedication}
        onChange={handleChange.bind(this, 'previousMigraineMedication')}
      />
    </div>
  </div>
));

const Certification = memo(({
  value: {
    hcpSignature,
    signatureDate,
  },
  onChange: handleChange = () => {}
}) => (
  <div className='section prescriber-information m-t-8'>
    <SectionLabel display='HCP Signature' />

    <div className='flex flex-column gap-16 p-4'>
      <p className='m-0'>I certify that the information contained in this form is complete and accurate to the best of my knowledge.</p>

      <p className='m-0'>This document serves as a prescription, referral form and statement of medically necessary for the patient referenced above.</p>

      <div className='flex align-items-center'>
        <SignatureWrapper
          name='hcpSignature'
          label='Electronically approved by:'
          value={hcpSignature}
          onChange={handleChange.bind(this, 'hcpSignature')}
        />
        <DateWrapper name='signatureDate' label='Date:' value={signatureDate} max={MaxDate} />
      </div>
    </div>
  </div>
));

const SectionA = ({
  value = {},
  className = '',
  onSubmit: handleSubmit = () => {},
  onChange: handleChange = () => {},
}) => (
  <div className={`page border-primary ${pageSelector} ${className}`}>
    <Heading value={value} onChange={handleChange} />
    <div className={`content flex flex-column ${pageElementSelector}`}>
      <PatientInformation value={value} onChange={handleChange} />
      <PrescriptionInformation value={value} onChange={handleChange} />
      <PrescriberInformation value={value} onChange={handleChange} />
      <MedicalNecessity value={value} onChange={handleChange} />
      <Certification value={value} onChange={handleChange} />
    </div>
    <Footer onSubmit={handleSubmit} />
  </div>
);

const SectionB = ({
  value = {},
  consultInfo = {},
  productInfo = {},
  className = '',
  heading: Heading = Fragment,
  onSubmit: handleSubmit = () => {},
  onChange: handleChange = () => {}
}) => {
  const { qAndAs } = useMemo(() => {
    try {
      const productIdsForPDFInSettings = productInfo.StoreFrontData.StorefrontApplicationSettings.get(PID_ProvidersAndCreatePDF);
      const productIds = productIdsForPDFInSettings.value.split(',').map((pId) => Number(pId));
      const allQAndAs = consultInfo.ProductConsultDetails;
      const qAndAs = allQAndAs.filter(({ ProductId }) => productIds.includes(ProductId));
      return { productIds, qAndAs };
    } catch(e) {
      return { productIds: [], qAndAs: [] };
    }
  }, [productInfo, consultInfo]);

  return (
    <QuestionAnswers
      value={value}
      qAndAs={qAndAs}
      className={className}
      heading={Heading}
      footer={Footer}
      onSubmit={handleSubmit}
      onChange={handleChange}
    />
  );
};

export const CMNTemplateForVyepti = ({
  id='',
  reference,
  className = '',
  value = {},
  notes = {},
  productInfo = {},
  patientInfo = {},
  consultInfo = {},
  onSubmit: handleSubmit = () => {},
  onChange: handleChange = () => {}
}) => (
  <div ref={reference} id={id} className={CMNVyeptiTemplateIdentifier}>
    <SectionA
      value={value}
      className={className}
      onSubmit={handleSubmit}
      onChange={handleChange}
    />

    <SectionC
      value={value}
      notes={notes}
      className={className}
      heading={Heading}
      onSubmit={handleSubmit}
      onChange={handleChange}
    />

    <SectionB
      value={value}
      consultInfo={consultInfo}
      productInfo={productInfo}
      className={className}
      heading={Heading}
      onSubmit={handleSubmit}
      onChange={handleChange}
    />
  </div>
);

export const TemplateDownloadManager = ({
  id = '',
  notes = {},
  productInfo = {},
  patientInfo = {},
  consultInfo = {},
  history = {},
  formField = { ...DEFAULT_FORMFIELD },

  getCmnPdfData = async () => {},
  setFormField = () => {}
}) => {
  const reference = useRef();

  const handleSubmit = (event) => {
    event.preventDefault();
    preparePDF(
      CMNVyeptiTemplateIdentifier,
      [pageSelector, pageWithMaxPossibleWidthSelector],
      Letter,
      pageElementSelector,
      ImageQuality[100],
      generatePDF
    ).then(() => {
      // code snippet if required
    });
  };

  const handleChange = (name, event) => {
    setFormField({ ...formField, [name]: event.target.value });
  };

  const loadData = async () => {
    const response  = await getCmnPdfData({
      OrderId: productInfo.OrderId,
      PaitentId: patientInfo.UserId,
      template: 'Vyepti'
    });

    const hasSubsequentFill = hasFullfilledOrder(history?.data || []);

    const pdfData = (() => {
      try {
        return response.data.Data || DEFAULT_PDF_INFO();
      } catch(e) {
        return DEFAULT_PDF_INFO();
      }
    })();

    let nextFormField = {
      ...DEFAULT_FORMFIELD,
    };

    try {
      nextFormField = {
        ...nextFormField,

        patientDob: moment(pdfData.patientInfo.dob).format(DateFormat),
        patientFullName: `${pdfData.patientInfo.lastName}, ${pdfData.patientInfo.firstName}`,
        patientHeight: patientInfo.Height,
        patientWeight: patientInfo.Weight,
        patientAddress: pdfData.patientInfo.address,
        patientState: pdfData.patientInfo.state,
        patientZip: pdfData.patientInfo.zip,
        patientCity: pdfData.patientInfo.city,
        patientPhone: pdfData.patientInfo.phone,
        patientEmail: pdfData.patientInfo.email,

        prescriberPreName: pdfData.prescriberInfo.preName,
        prescriberFullName: `${pdfData.prescriberInfo.lastName}, ${pdfData.prescriberInfo.firstName}`,
        prescriberPracticeName: DEFAULT_FORMFIELD_WHEN_FIELD_IS_MISSING.prescriberPracticeName,
        prescriberNpi: pdfData.prescriberInfo.npiNo,
        prescriberFax: DEFAULT_FORMFIELD_ONLY_FOR_VYEPTI.prescriberFax,

        hcpSignature: `${pdfData.provider.signature.prifix} ${pdfData.provider.signature.firstName} ${pdfData.provider.signature.lastNmae}`,
        signatureDate: MaxDate,
      };
    } catch(e) {

    }

    if (hasSubsequentFill) {
      nextFormField.refills = 3;
    }

    // eslint-disable-next-line no-unused-vars
    const questionAnswersMap = new Map([[0, []]]);
    const questionAnswers = [];

    consultInfo.ProductConsultDetails.forEach(({ QuestionAnswers = [] }) => {
      questionAnswers.push(...QuestionAnswers);
    });

    questionAnswers.forEach((qAndA) => {
      const qAndAs = [qAndA];
      if (questionAnswersMap.has(qAndA.QuestionId)) {
        const _qAndAs = questionAnswersMap.get(qAndA.QuestionId);
        qAndAs.push(..._qAndAs);
      }
      questionAnswersMap.set(qAndA.QuestionId, qAndAs);
    });

    const config = (() => {
      try {
        return new Map(productInfo.StoreFrontData.StorefrontApplicationSettings) || new Map([['', {}]]);
      } catch(e) {
        return new Map([['', {}]]);
      }
    })();

    if (config.has(QID_NewToVyeptiCheckbox)) {
      const qIds = config.get(QID_NewToVyeptiCheckbox).value.split(',');
      for (let i = 0; i < qIds.length; i++) {
        const qId = Number(qIds[i]);
        if (!questionAnswersMap.has(qId)) continue;

        const qAndAs = questionAnswersMap.get(qId);
        for (let j = 0; j < qAndAs.length; j++) {
          const qAndA = qAndAs[j];

          const regex = /(no)/gmi;
          if (regex.test(qAndA.AnswerText)) {
            nextFormField['checkbox-new-patient'] = CheckBoxChecked.yes;
            break;
          }
        }

        if (nextFormField['checkbox-new-patient'] === CheckBoxChecked.yes) {
          break;
        }
      }
    }

    if (config.has(QID_MigrainStartDateInput)) {
      const qIds = config.get(QID_MigrainStartDateInput).value.split(',');
      for (let i = 0; i < qIds.length; i++) {
        const qId = Number(qIds[i]);
        if (!questionAnswersMap.has(qId)) continue;

        const qAndAs = questionAnswersMap.get(qId);
        for (let j = 0; j < qAndAs.length; j++) {
          const qAndA = qAndAs[j];

          if (qAndA?.AnswerText?.length > 0) {
            const { hasAnswer, answer } = evaluateAnswerFromLastTreatmentDateInput(qAndA);
            if (hasAnswer) {
              nextFormField.migrainStartDate = answer;
              break;
            }
          }
        }

        if (nextFormField.migrainStartDate !== '') {
          break;
        }
      }
    }

    if (config.has(QID_HeadacheDaysPerMonthInput)) {
      const qIds = config.get(QID_HeadacheDaysPerMonthInput).value.split(',');
      nextFormField.headacheDaysPerMonth = 'Unknown';

      for (let i = 0; i < qIds.length; i++) {
        const qId = Number(qIds[i]);
        if (!questionAnswersMap.has(qId)) continue;

        const qAndAs = questionAnswersMap.get(qId);
        for (let j = 0; j < qAndAs.length; j++) {
          const qAndA = qAndAs[j];
          const { hasAnswer, answer } = evaluateAnswerFromHeadacheDaysInput(qAndA);
          if (hasAnswer) {
            nextFormField.headacheDaysPerMonth = answer;
            break;
          }
        }

        if (nextFormField.headacheDaysPerMonth !== 'Unknown') break;
      }
    }

    if (config.has(QID_LastTreatmentDateInput)) {
      const qIds = config.get(QID_LastTreatmentDateInput).value.split(',');
      for (let i = 0; i < qIds.length; i++) {
        const qId = Number(qIds[i]);
        if (!questionAnswersMap.has(qId)) continue;

        const qAndAs = questionAnswersMap.get(qId);
        for (let j = 0; j < qAndAs.length; j++) {
          const qAndA = qAndAs[j];

          if (qAndA?.AnswerText?.length > 0) {
            const { hasAnswer, answer } = evaluateAnswerFromLastTreatmentDateInput(qAndA);
            if (hasAnswer) {
              nextFormField.lastTreatmentDate = answer;
              break;
            }
          }
        }

        if (nextFormField.lastTreatmentDate !== '') {
          break;
        }
      }

      /*
      if (nextFormField.lastTreatmentDate === '') {
        nextFormField.lastTreatmentDate = MaxMonthAndYear;
      }
      */
    }

    try {
      if (config.has(QID_DrugAllergiesInput)) {
        const qIds = config.get(QID_DrugAllergiesInput).value.split(',');
        const drugAllergies = [];
        nextFormField.drugAllergies = '';

        for (let i = 0; i < qIds.length; i++) {
          const qId = Number(qIds[i]);
          if (!questionAnswersMap.has(qId)) continue;

          const qAndAs = questionAnswersMap.get(qId);
          for (let j = 0; j < qAndAs.length; j++) {
            const qAndA = qAndAs[j];

            if (qAndA?.AnswerText?.length > 0) {
              const dAs = qAndA.AnswerText.split('\n');
              drugAllergies.push(...dAs);
            }
          }
        }

        nextFormField.drugAllergies = drugAllergies.map((dA) => dA.trim()).join(', ');
      }
    } catch (e) {

    }

    if (config.has(QID_PreviousMigraineMedicationInput)) {
      const qIds = config.get(QID_PreviousMigraineMedicationInput).value.split(',');
      const medications = [];
      nextFormField.previousMigraineMedication = '';

      for (let i = 0; i < qIds.length; i++) {
        const qId = Number(qIds[i]);
        if (!questionAnswersMap.has(qId)) continue;

        const qAndAs = questionAnswersMap.get(qId);
        for (let j = 0; j < qAndAs.length; j++) {
          const qAndA = qAndAs[j];
          if (!qAndA?.AnswerText?.length) continue;

          const medication = qAndA.AnswerText.trim();
          if (!medication?.length) continue;

          const regex = /^(other)$/gmi;
          if (!regex.test(medication)) {
            medications.push(medication);
          }
        }
      }

      nextFormField.previousMigraineMedication = medications.join(', ');
    }

    console.log('@logger - initializing the pdf template', {
      config,
      pdfData,
      pdfInfo: productInfo.pdfInfo,
      formField: nextFormField,
    });

    setFormField(nextFormField);
  };

  useEffect(loadData, []);

  return (
    <CMNTemplateForVyepti
      id={id}
      reference={reference}
      className='m-b-4 p-4'
      value={formField}
      notes={notes}
      productInfo={productInfo}
      patientInfo={patientInfo}
      consultInfo={consultInfo}
      onSubmit={handleSubmit}
      onChange={handleChange}
    />
  );
};

export default memo(TemplateDownloadManager);
